import { createContext, ReactNode, useContext, useState } from "react";
import { LinearProgress } from "@mui/material";

// Types
interface LinearProgressContextType {
	isLoading: boolean;
	progress: number;
	showProgress: () => void;
	hideProgress: () => void;
	updateProgress: (value: number) => void;
	setIsLoading: (loading: boolean) => void;
}

interface LinearProgressProviderProps {
	children: ReactNode;
}

// Create the context
const LinearProgressContext = createContext<LinearProgressContextType | undefined>(
	undefined
);

// Provider component
export const LinearProgressProvider: React.FC<LinearProgressProviderProps> = ({
	children
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [progress, setProgress] = useState(0);

	const showProgress = () => setIsLoading(true);
	const hideProgress = () => setIsLoading(false);
	const updateProgress = (value: number) => setProgress(value);

	const value = {
		isLoading,
		progress,
		showProgress,
		hideProgress,
		updateProgress,
		setIsLoading
	};

	return (
		<LinearProgressContext.Provider value={value}>
			{children}
			{isLoading && (
				<LinearProgress
					variant={progress > 0 ? "determinate" : "indeterminate"}
					value={progress > 0 ? progress : undefined}
					sx={{
						position: "fixed",
						top: 0,
						bottom: 0,
						left: 0,
						right: 0,
						width: "100%",
						zIndex: 3
					}}
				/>
			)}
		</LinearProgressContext.Provider>
	);
};

// Custom hook to use the progress context
export const useLinearProgressContext = (): LinearProgressContextType => {
	const context = useContext(LinearProgressContext);
	if (!context) {
		throw new Error(
			"useLinearProgressContext must be used within a LinearProgressProvider"
		);
	}
	return context;
};
